import dot from 'dot-prop-immutable';
import { forEachObjIndexed, pathOr } from 'ramda';
import { delay } from 'redux-saga';
import {
  all,
  call,
  cancelled,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { invalidateAllActionsQueries } from '@frontend/api';
import { createStandaloneToast, theme } from '@frontend/legacy-components';

import { DETAILS_SETTINGS_MODAL_TYPES } from 'constants/all';
import { COMPONENT_BASIC_UPDATE } from 'constants/performance';

import { detailsSettingsModalFlow } from 'modules/common/modals/detailsSettingsModal/data/sagas';
import {
  materialModal,
  types as modalTypes,
} from 'modules/common/modals/supplierMaterialModal/data/actions';
import * as opDefSelectors from 'modules/opDefs/data/reducer';
import * as collaboratorsSagas from 'modules/quotes/collaborators/data/sagas';
import { getQuoteItemHasCollaborator } from 'modules/quotes/collaborators/data/selectors';
import {
  broadcastComponentCostingChangeFromBaqToQpv,
  broadcastExportControlledChange,
  broadcastPartDescriptorChange,
  broadcastQuoteItemWorkflowChange,
} from 'modules/quotes/quoteDetailsPage/hooks/useSyncQpvAndBaqTabs';
import { assemblyTree } from 'modules/quotes/quoteItemComponents/assemblyTree/data/actions';
import {
  componentInterrogation,
  expandComponent,
} from 'modules/quotes/quoteItemComponents/data/actions';
import { getExpandedComponentId } from 'modules/quotes/quoteItemComponents/data/reducer';
import {
  getJobHistoryKey,
  getQuoteHistoryKey,
  getSimilarPartsKey,
} from 'modules/quotes/quoteItems/data/hooks';
import quoteItemService from 'modules/quotes/quoteItems/data/service';
import {
  invalidateAllBomTableQueries,
  invalidateBomTableQueriesForComponentIds,
} from 'modules/quotes/quoteItems/data/utils/bomTable';
import { livePricingPreview } from 'modules/quotes/quoteItems/modals/Operation/data/actions';
import { getCurrentOverrides } from 'modules/quotes/quoteItems/modals/Operation/data/reducer';

import partService from 'services/partService';
import quoteService from 'services/quoteService';

import { pagePending } from 'store/app/actions';
import { receiveSupplierMaterialFromGetOrCreate } from 'store/materials/actions';
import { getMaterialById } from 'store/materials/reducer';
import { receivePart } from 'store/part/actions';
import { saveQuoteSettingsById } from 'store/quotes/actions';
import { getOrCreateQuote } from 'store/quotes/getOrCreateQuote';
import { constructTempQuoteItemData } from 'store/quotes/sagas';
import {
  getComponentById,
  getQuantity as selectQuantity,
  getQuote,
  getQuoteNumberWithRevision,
} from 'store/quotes/selectors';

import { queryClient } from 'utils/QueryClientProvider';
import history from 'utils/browserHistory';
import { reorderByPosition } from 'utils/helpers';
import { performanceMeasureMarkFinished } from 'utils/hooks';
import { takeLatestDeep } from 'utils/reduxUtils';
import { getErrorMessage, pluralize } from 'utils/stringUtils';

import {
  briefComponent,
  componentUpdatePending,
  createAddOn,
  createDiscount,
  createExpedite,
  createOperation,
  createProfitItem,
  createQuantity,
  createQuoteItemWithManualPart,
  deleteAddOn,
  deleteDiscount,
  deleteExpedite,
  deleteOperation,
  deleteProfitItem,
  deleteQuantity,
  expandQuoteItem,
  forceComponentRegeneration,
  forceQuoteItemRecalculation,
  initialBriefComponentFetch,
  lockQuoteItem,
  purchasedComponentAdd,
  receiveAddOns,
  receiveComponent,
  receiveDiscounts,
  receiveProfitItems,
  receiveQuantities,
  receiveQuoteItem,
  receiveQuoteItems,
  receiveTemporaryQuoteItemIds,
  refreshBomRollup,
  refreshQuoteItem,
  reorderAddOns,
  reorderDiscounts,
  reorderOperations,
  reorderProfitItems,
  selectFinishOps,
  updateAddOn,
  updateAddOnCell,
  updateAddOnOverrides,
  updateCell,
  updateComponent,
  updateDiscount,
  updateDiscountCell,
  updateDiscountOverrides,
  updateExpedite,
  updateMetadata,
  updateOperation,
  updateOperationIsOutsideService,
  updateOperationOverrides,
  updatePartFilename,
  updateProfitItem,
  updateProfitItemCell,
  updateProfitItemOverrides,
  updateQuantities,
  updateQuantity,
  updateQuoteItem,
  updateQuoteItemWorkflowStep,
  updateSupplierMaterial,
} from './actions';
import * as selectors from './selectors';
import service from './service';
import * as types from './types';

const { toast } = createStandaloneToast({ theme });

export default function* root() {
  yield takeLatest(types.ADD_ON_CREATE_REQUEST, watchCreateAddOn);
  yield takeLatest(types.ADD_ON_DELETE_REQUEST, watchDeleteAddOn);
  yield takeEvery(types.ADD_ON_UPDATE_REQUEST, watchUpdateAddOn);
  yield takeEvery(types.ADD_ON_CELL_UPDATE_REQUEST, watchUpdateAddOnCell);
  yield takeEvery(
    types.UPDATE_ADD_ON_OVERRIDES_REQUEST,
    watchUpdateAddOnOverrides,
  );
  yield takeLatest(types.REORDER_ADD_ONS_REQUEST, watchReorderAddOns);
  yield takeEvery(types.EXPAND_QUOTE_ITEM_REQUEST, watchExpandQuoteItem);
  yield takeEvery(
    types.FETCH_INITIAL_BRIEF_COMPONENTS_REQUEST,
    watchInitialFetchBriefComponents,
  );
  yield takeEvery(types.TAKE_OVER_QUOTE_ITEM_REQUEST, watchTakeOverQuoteItem);
  yield takeEvery(types.COLLAPSE_QUOTE_ITEM, watchCollapseQuoteItem);
  yield takeEvery(types.CREATE_OPERATION_REQUEST, watchCreateOperation);
  yield takeEvery(types.DELETE_OPERATION_REQUEST, watchDeleteOperation);
  yield takeLatest(types.CREATE_QUANTITY_REQUEST, watchCreateQuantity);
  yield takeLatest(types.DELETE_QUANTITY_REQUEST, watchDeleteQuantity);
  yield takeEvery(types.UPDATE_QUANTITY_REQUEST, watchUpdateQuantity);
  yield takeLatest(types.UPDATE_QUANTITIES_REQUEST, watchUpdateQuantities);
  yield takeLatest(types.UPDATE_OPERATION_REQUEST, watchUpdateOperation);
  yield takeLatest(types.REORDER_OPERATIONS_REQUEST, watchReorderOperations);
  yield takeLatest(types.UPDATE_QUOTE_ITEM_REQUEST, watchUpdateQuoteItem);
  yield takeEvery(types.UPDATE_COMPONENT_REQUEST, watchUpdateComponent);
  yield takeEvery(types.UPDATE_CELL_REQUEST, watchUpdateCell);
  yield takeLatestDeep(
    types.UPDATE_OPERATION_OVERRIDES_REQUEST,
    (action) => action.payload.operationId,
    watchUpdateOperationOverride,
  );
  yield takeEvery(types.SELECT_FINISH_OPS_REQUEST, watchSelectFinishOps);
  yield takeLatestDeep(
    types.CREATE_EXPEDITE_REQUEST,
    (action) => action.payload.quantityId,
    watchCreateExpedite,
  );
  yield takeLatestDeep(
    types.UPDATE_EXPEDITE_REQUEST,
    (action) => action.payload.expediteId,
    watchUpdateExpedite,
  );
  yield takeLatestDeep(
    types.DELETE_EXPEDITE_REQUEST,
    (action) => action.payload.expediteId,
    watchDeleteExpedite,
  );
  yield takeLatest(
    types.UPDATE_SUPPLIER_MATERIAL_REQUEST,
    watchUpdateSupplierMaterial,
  );
  yield takeLatest(
    types.FORCE_QUOTE_ITEM_RECALCULATION_REQUEST,
    watchQuoteItemRecalculation,
  );
  yield takeLatest(
    types.FORCE_COMPONENT_REGENERATION_REQUEST,
    watchComponentRegeneration,
  );
  yield takeEvery(types.REFRESH_COMPONENT, watchRefreshComponent);
  yield takeEvery(types.REFRESH_QUOTE_ITEM, watchRefreshQuoteItem);
  yield takeEvery(types.BRIEF_COMPONENT_ADD, watchAddBriefComponent);
  yield takeEvery(types.BRIEF_COMPONENT_REFRESH, watchRefreshBriefComponent);
  yield takeEvery(
    types.BRIEF_COMPONENT_REFRESH_MULTIPLE,
    watchRefreshMultipleBriefComponents,
  );
  yield takeLatest(
    types.PURCHASED_COMPONENT_ADD_REQUEST,
    watchAddPurchasedComponents,
  );
  yield takeLatest(
    types.UPDATE_OPERATION_IS_OUTSIDE_SERVICE_REQUEST,
    watchUpdateOutsideService,
  );
  yield takeLatest(
    types.CREATE_QUOTE_ITEM_WITH_MANUAL_PART_REQUEST,
    watchCreateQuoteItemWithManualPart,
  );
  yield takeLatest(types.UPDATE_PART_FILENAME_REQUEST, watchUpdatePartFilename);
  yield takeLatest(types.REFRESH_BOM_ROLLUP_REQUEST, watchRefreshBomRollup);
  yield takeEvery(
    types.UPDATE_QUOTE_ITEM_WORKFLOW_STEP_REQUEST,
    watchUpdateQuoteItemWorkflowStep,
  );
  yield takeLatest(types.PROFIT_ITEM_CREATE_REQUEST, watchCreateProfitItem);
  yield takeLatest(types.PROFIT_ITEM_DELETE_REQUEST, watchDeleteProfitItem);
  yield takeEvery(types.PROFIT_ITEM_UPDATE_REQUEST, watchUpdateProfitItem);
  yield takeEvery(
    types.PROFIT_ITEM_CELL_UPDATE_REQUEST,
    watchUpdateProfitItemCell,
  );
  yield takeEvery(
    types.UPDATE_PROFIT_ITEM_OVERRIDES_REQUEST,
    watchUpdateProfitItemOverrides,
  );
  yield takeLatest(types.REORDER_PROFIT_ITEMS_REQUEST, watchReorderProfitItems);
  yield takeLatest(types.DISCOUNT_CREATE_REQUEST, watchCreateDiscount);
  yield takeLatest(types.DISCOUNT_DELETE_REQUEST, watchDeleteDiscount);
  yield takeEvery(types.DISCOUNT_UPDATE_REQUEST, watchUpdateDiscount);
  yield takeEvery(types.DISCOUNT_CELL_UPDATE_REQUEST, watchUpdateDiscountCell);
  yield takeEvery(
    types.UPDATE_DISCOUNT_OVERRIDES_REQUEST,
    watchUpdateDiscountOverrides,
  );
  yield takeLatest(types.REORDER_DISCOUNTS_REQUEST, watchReorderDiscounts);
  yield takeLatest(types.UPDATE_METADATA_REQUEST, watchUpdateMetadata);

  yield takeEvery(
    types.UPDATE_COMPONENT_SUCCESS,
    watchUpdateComponentQuoteItemForTabSyncing,
  );
  yield takeEvery(
    types.UPDATE_QUOTE_ITEM_SUCCESS,
    watchUpdateComponentQuoteItemForTabSyncing,
  );
}

function* watchInitialFetchBriefComponents({ payload }) {
  const { quoteItemId } = payload;
  try {
    invalidateAllBomTableQueries();
    const briefComponents = yield call(
      quoteItemService.briefComponentsList,
      quoteItemId,
    );

    yield put(
      initialBriefComponentFetch.success({ briefComponents, quoteItemId }),
    );
  } catch (error) {
    yield put(initialBriefComponentFetch.error({ quoteItemId }));
    console.error(error);
  }
}

export function* watchRefreshComponent(action) {
  const { componentId, params } = action.payload;
  yield call(refreshComponent, componentId, params);
}

export function* refreshComponent(componentId, params) {
  const component = yield call(service.components.get, componentId, params);
  yield put(receiveComponent(component));
  return component;
}

function* watchRefreshBriefComponent(action) {
  const { componentId } = action.payload;
  yield call(refreshBriefComponent, componentId);
}
function* watchRefreshMultipleBriefComponents(action) {
  const { componentIds, quoteItemId } = action.payload;
  yield call(refreshMultipleBriefComponents, quoteItemId, componentIds);
}

function* refreshBriefComponent(componentId) {
  const newBriefComponent = yield call(
    service.components.getBrief,
    componentId,
  );
  yield put(briefComponent.receive({ briefComponent: newBriefComponent }));
}
function* refreshMultipleBriefComponents(quoteItemId, componentIds) {
  const newBriefComponents = yield call(
    quoteItemService.briefComponentsList,
    quoteItemId,
    componentIds,
  );
  yield put(
    briefComponent.receiveMultiple({ briefComponents: newBriefComponents }),
  );
}

function* refreshAddOns(componentId) {
  const addOns = yield call(service.addOns.list, componentId);
  yield put(receiveAddOns(addOns));
}

function* refreshQuantity(quantityId) {
  const response = yield call(service.quantities.get, quantityId);
  yield put(receiveQuantities([response]));
}

export function* refreshQuantities(componentId) {
  const quantities = yield call(service.quantities.list, componentId);
  yield put(receiveQuantities(quantities));
}

export function* refreshQuoteItemSaga(quoteItemId) {
  const nextQuoteItem = yield call(quoteService.quoteItems.get, quoteItemId);
  yield put(receiveQuoteItem(nextQuoteItem));
  invalidateAllBomTableQueries();

  // if there is an expanded component, we need to refresh that as well
  yield call(refreshExpandedComponent);
}

export function* recalculateQuoteItemSaga(quoteItemId, componentId) {
  const nextQuoteItem = yield call(quoteService.quoteItems.get, quoteItemId, {
    refresh: true,
  });
  yield put(receiveQuoteItem(nextQuoteItem));
  invalidateAllBomTableQueries();

  if (componentId) {
    queryClient.invalidateQueries({
      queryKey: getSimilarPartsKey(componentId),
    });
    queryClient.invalidateQueries({
      queryKey: getJobHistoryKey(componentId),
      refetchType: 'all',
    });
    queryClient.invalidateQueries({
      queryKey: getQuoteHistoryKey(componentId),
      refetchType: 'all',
    });
  }

  // if there is an expanded component, we need to refresh that as well
  yield call(refreshExpandedComponent);
}

function* watchRefreshQuoteItem(action) {
  const { quoteItemId, shouldRecalculate } = action.payload;
  if (shouldRecalculate) {
    yield call(recalculateQuoteItemSaga, quoteItemId);
  } else {
    yield call(refreshQuoteItemSaga, quoteItemId);
  }
}

function* watchQuoteItemRecalculation(action) {
  const { quoteItemId, componentId } = action.payload;
  try {
    yield call(recalculateQuoteItemSaga, quoteItemId, componentId);
    yield put(forceQuoteItemRecalculation.success({ quoteItemId }));
  } catch (e) {
    yield put(forceQuoteItemRecalculation.error({ quoteItemId }));
  }
}

function* watchTakeOverQuoteItem(action) {
  const { quoteItemId, replaceHistory } = action.payload;
  const { isCancelled } = yield call(
    collaboratorsSagas.acquireLock,
    quoteItemId,
  );

  if (isCancelled) {
    return;
  }
  yield put(expandQuoteItem.request({ quoteItemId, replaceHistory }));
}

function* watchExpandQuoteItem(action) {
  const { quoteItemId, replaceHistory } = action.payload;

  try {
    if (!location.pathname.includes(quoteItemId.toString())) {
      const quoteNumber = yield select(getQuoteNumberWithRevision);
      const navigationAction = replaceHistory ? history.replace : history.push;
      navigationAction(`/quotes/edit/${quoteNumber}/${quoteItemId}`, {
        noScrollRestoration: true,
      });
    }

    const quoteItem = yield call(quoteService.quoteItems.get, quoteItemId);
    yield all([
      put(expandQuoteItem.success({ quoteItem })),
      put(initialBriefComponentFetch.request({ quoteItemId })),
    ]);

    const hasCollaborator = yield select(
      getQuoteItemHasCollaborator,
      quoteItemId,
    );
    if (hasCollaborator) yield put(lockQuoteItem({ quoteItemId }));
  } catch (error) {
    console.error(error);
    put(expandQuoteItem.error({ quoteItemId }));
    yield call(toast, {
      status: 'error',
      title: 'Error',
      description: "Can't expand quote item",
    });
  }
}

function* watchCollapseQuoteItem() {
  const quoteItemId = yield select(selectors.getExpandedQuoteItemId);
  if (quoteItemId) {
    yield call(collaboratorsSagas.removeLock, quoteItemId);
  }

  const quoteNumber = yield select(getQuoteNumberWithRevision);
  history.push(`/quotes/edit/${quoteNumber}`, { noScrollRestoration: true });
}

function* watchCreateQuantity(action) {
  try {
    const { componentId, quantity } = action.payload;
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    )
      return;
    yield call(service.quantities.create, componentId, quantity);
    const quoteItemId = yield select(
      selectors.getQuoteItemIdFromComponentId,
      componentId,
    );
    yield call(refreshExpandedComponent);
    yield put(
      refreshQuoteItem({
        quoteItemId,
        shouldRecalculate: false,
      }),
    );
  } catch (error) {
    yield put(createQuantity.error(error));
  }
}

function* watchDeleteQuantity(action) {
  try {
    const { componentId, quantityId } = action.payload;
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    )
      return;
    yield call(service.quantities.delete, quantityId);
    const component = yield select(getComponentById, componentId);
    const nextComponent = {
      ...component,
      quantities: component.quantities.filter(
        (quantity) => quantity.id != quantityId,
      ),
    };
    const quoteItemId = yield select(
      selectors.getQuoteItemIdFromComponentId,
      componentId,
    );
    yield call(refreshQuoteItemSaga, quoteItemId);
    yield put(updateComponent.success({ component: nextComponent }));
  } catch (error) {
    yield put(deleteQuantity.error(error));
  }
}

const hasAnyProperty = (obj, names) =>
  names.map((name) => name in obj).indexOf(true) !== -1;

function* watchUpdateQuantity(action) {
  try {
    const { quantityId, data } = action.payload;
    const { componentId } = yield select(selectQuantity, quantityId);
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    )
      return;
    const response = yield call(service.quantities.update, quantityId, data);

    yield put(updateQuantity.success(response));
    // if yield is changed on an assembly component, we need to refresh the whole quote item
    // if quantity is not the root component otherwise, we have to refresh root component quantities
    const expandedQuoteItem = yield select(selectors.getExpandedQuoteItem);
    const expandedId = expandedQuoteItem.id;
    const isRootComponent = componentId == expandedQuoteItem.rootComponent;
    const yieldUpdated = hasAnyProperty(data, ['manualYieldPct']);
    if (isRootComponent && yieldUpdated) {
      yield put(
        refreshQuoteItem({
          quoteItemId: expandedId,
          shouldRecalculate: false,
        }),
      );
    } else if (!isRootComponent) {
      yield call(refreshBomRollupForComponentParentsById, { componentId });
    }
    yield call(refreshExpandedComponent);
    const mostLikelyWonQuantityUpdated = hasAnyProperty(data, [
      'isMostLikelyWonQuantity',
    ]);
    if (mostLikelyWonQuantityUpdated) {
      yield call(refreshQuantities, expandedQuoteItem.rootComponent);
    }
  } catch (error) {
    yield put(updateQuantity.error(error));
  }
}

function* watchUpdateQuantities(action) {
  // TODO: delete when colors of money is dead (this is only used to update legacy markup fields)
  try {
    const { componentId, data } = action.payload;
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    )
      return;
    const response = yield call(
      service.quantities.bulkUpdate,
      componentId,
      data,
    );

    yield put(updateQuantities.success(response));
  } catch (error) {
    yield put(updateQuantities.error(error));
  }
}

function* watchUpdateCell(action) {
  const { cellId, quantityId, data, cb } = action.payload;

  try {
    const { operationId } = yield select(selectors.getCell, cellId);

    let isCellUpNext = yield select(
      selectors.getIsCellNextInCalculationQueue,
      cellId,
      quantityId,
    );
    while (!isCellUpNext) {
      yield race({
        error: take(types.UPDATE_CELL_ERROR),
        success: take(types.UPDATE_CELL_SUCCESS),
      });
      isCellUpNext = yield select(
        selectors.getIsCellNextInCalculationQueue,
        cellId,
        quantityId,
      );
    }

    const { componentId } = yield select(selectors.getOperation, operationId);
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    ) {
      return yield put(updateCell.error({ cellId, quantityId }));
    }
    const response = yield call(service.cells.update, cellId, data);

    if (cb) {
      cb();
    }
    yield put(updateCell.success(response));

    // only call for side effects if other cells are not also updating around
    // this one to make the rapid edit experience as snappy as possible
    const areMultipleUpdating = yield select(
      selectors.getAreMultipleCellsUpdatingForQuantity,
      quantityId,
    );
    if (!areMultipleUpdating) {
      yield all([
        call(refreshQuantity, response.quantityId),
        call(refreshBomRollupForComponentParentsById, { componentId }),
      ]);
    }
  } catch (error) {
    yield put(updateCell.error({ error, cellId, quantityId }));
  }
}

function* watchUpdateOperation(action) {
  try {
    const { operationId, data } = action.payload;
    const { componentId } = yield select(selectors.getOperation, operationId);
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    ) {
      return yield put(updateOperation.error());
    }
    const response = yield call(service.operations.update, operationId, data);

    yield put(updateOperation.success({ data: [response], operationId }));

    if ('name' in data) {
      // if name is in the data, we need to refresh quantities because of price
      // dict lookup side effects
      yield all([
        call(refreshQuantities, componentId),
        call(refreshBomRollupForComponentParentsById, {
          componentId,
        }),
      ]);
    }
  } catch (error) {
    console.error(error);
    yield put(updateOperation.error());
  }
}

function* watchUpdateOutsideService(action) {
  const { payload } = action;
  const { operationId, isOutsideService } = payload;

  try {
    const { componentId } = yield select(selectors.getOperation, operationId);
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    ) {
      return yield put(
        updateOperationIsOutsideService.error('Quote item is locked'),
      );
    }

    const response = yield call(service.operations.update, operationId, {
      isOutsideService,
    });

    // perform surrounding updates and refresh live pricing since this action
    // happens in the operation drawer
    const currentOverrides = yield select(getCurrentOverrides);
    yield put(livePricingPreview.request(operationId, currentOverrides));

    yield put(updateOperationIsOutsideService.success(response));

    // Now update surrounding form
    yield all([
      call(refreshQuantities, componentId),
      call(refreshBomRollupForComponentParentsById, {
        componentId,
      }),
    ]);
  } catch (error) {
    yield put(updateOperationIsOutsideService.error(error));
  }
}

function* watchCreateOperation(action) {
  const { componentId, data } = action.payload;
  const category = pathOr(null, ['payload', 'data', 'category'], action);
  try {
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    ) {
      yield put(createOperation.error({ category }));
      return;
    }

    const response = yield call(service.operations.create, componentId, data);
    if (response.taskSet && response.taskSet.length) {
      yield put(componentInterrogation.request({ componentId }));
    }

    // TODO: remove with https://paperlessparts.atlassian.net/browse/PP-23862
    // refresh actions since new operations can influence resolutions
    invalidateAllActionsQueries();

    yield all([
      call(refreshComponent, componentId),
      call(refreshBomRollupForComponentParentsById, {
        componentId,
      }),
    ]);

    // broadcast change to QPV
    const { part: partUuid, quoteItemId } = yield select(
      selectors.getComponentById,
      componentId,
    );
    broadcastComponentCostingChangeFromBaqToQpv({
      context: {
        partUuid,
        componentId,
        quoteItemId,
      },
    });

    yield put(createOperation.success(response.category));
  } catch (error) {
    //`error` is usually FetchError from `@frontend/utils` and `error.message` is not 'own' property
    //So message can't be destructured with `...error`. It has to be added manually if defined.
    const message = error != null && typeof error === 'object' && error.message;
    yield put(
      createOperation.error({
        ...error,
        ...(message && { message }),
        category,
      }),
    );
  }
}

function* watchDeleteOperation(action) {
  try {
    const { componentId, operationId } = action.payload;
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    )
      return;
    yield call(service.operations.delete, operationId);

    // TODO: remove with https://paperlessparts.atlassian.net/browse/PP-23862
    // refresh actions since process changes can influence resolutions
    invalidateAllActionsQueries();

    // refresh operations due to workpiece side effects
    const operations = yield call(service.operations.list, componentId);
    const component = yield select(getComponentById, componentId);

    const nextComponent = {
      ...component,
      operations: operations,
    };
    yield put(receiveComponent(nextComponent));

    yield all([
      call(refreshQuantities, componentId),
      call(refreshBomRollupForComponentParentsById, {
        componentId,
      }),
    ]);

    // broadcast costing change
    const { part: partUuid, quoteItemId } = yield select(
      selectors.getComponentById,
      componentId,
    );
    broadcastComponentCostingChangeFromBaqToQpv({
      context: {
        partUuid,
        componentId,
        quoteItemId,
      },
    });
  } catch (error) {
    yield put(deleteOperation.error(error));
  }
}

/**
 * Function to handle adding brief components to the store when they could have
 * had a new material associated with them...which means we have to add the new
 * supplier material to the store.
 * Used as a side effect for adding manual components and uploading manual
 * components.
 */
function* watchAddBriefComponent(action) {
  const { component } = action.payload;

  // receive new material details from newly created component if necessary
  const { material } = component;

  if (material) {
    const newSupplierMaterial = {
      ...material,
      materialId: material.material.id,
    };
    yield put(
      receiveSupplierMaterialFromGetOrCreate({
        supplierMaterial: newSupplierMaterial,
      }),
    );
  }

  yield all([
    put(briefComponent.receive({ briefComponent: component })),
    put(assemblyTree.markNeedsUpdate({})),
  ]);
}

function* watchReorderOperations(action) {
  try {
    const { componentId, oldPosition, nextPosition } = action.payload;

    const component = yield select(getComponentById, componentId);

    // hack: update positions locally, the database will be updated on the backend
    const reorderedOperations = reorderByPosition({
      arrayToReorder: component.operations,
      oldPosition,
      nextPosition,
    }).filter((op) => !!op.id); // hack: for when the frontend and backend are out of sync

    const nextComponent = {
      ...component,
      operations: reorderedOperations,
    };
    yield put(updateComponent.success({ component: nextComponent }));
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    )
      return;

    // actually update database
    const operation = component.operations.find(
      (op) => op.position === oldPosition,
    );

    yield call(service.operations.update, operation.id, {
      position: nextPosition >= oldPosition ? nextPosition - 1 : nextPosition,
    });

    const operations = yield call(service.operations.list, componentId);
    yield put(reorderOperations.success(operations));

    yield all([
      call(refreshQuantities, componentId),
      call(refreshBomRollupForComponentParentsById, {
        componentId,
      }),
    ]);
  } catch (error) {
    yield put(reorderOperations.error(error));
  }
}

function* watchReorderAddOns(action) {
  try {
    const { componentId, oldPosition, nextPosition } = action.payload;

    const component = yield select(getComponentById, componentId);

    const reorderedAddOns = reorderByPosition({
      arrayToReorder: component.addOns,
      oldPosition,
      nextPosition,
    });

    const nextComponent = {
      ...component,
      addOns: reorderedAddOns,
    };
    yield put(updateComponent.success({ component: nextComponent }));
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    )
      return;
    // actually update database
    const addOn = component.addOns.find(
      (addOn) => addOn.position === oldPosition,
    );
    yield call(service.addOns.update, addOn.id, {
      position: nextPosition >= oldPosition ? nextPosition - 1 : nextPosition,
    });

    yield all([call(refreshAddOns, componentId)]);
    yield put(reorderAddOns.success());

    yield call(refreshQuantities, componentId);
  } catch (error) {
    yield put(reorderAddOns.error(error));
  }
}

function* watchUpdateQuoteItem(action) {
  const { quoteItemId, data } = action.payload;
  if (
    yield call(collaboratorsSagas.areQuoteItemsLocked, {
      quoteItemIds: [quoteItemId],
    })
  )
    return;
  try {
    const response = yield call(
      quoteService.quoteItems.update,
      quoteItemId,
      data,
    );
    yield put(updateQuoteItem.success({ quoteItem: response, data }));

    if (hasAnyProperty(data, ['workflowStatus'])) {
      yield put(assemblyTree.markNeedsUpdate({}));
    }
  } catch (error) {
    yield put(updateQuoteItem.error(error));
  }
}

function* watchUpdateMetadata(action) {
  const { quoteItemId, metadata } = action.payload;
  if (
    yield call(collaboratorsSagas.areQuoteItemsLocked, {
      quoteItemIds: [quoteItemId],
    })
  )
    return;
  try {
    const response = yield call(quoteService.quoteItems.update, quoteItemId, {
      metadata,
    });
    yield put(
      updateMetadata.success({ quoteItem: response, data: { metadata } }),
    );
  } catch (error) {
    yield put(updateMetadata.error(error));
  }
}

// TODO: parallelize network calls for update conditions
function* watchUpdateComponent(action) {
  const { componentId, data } = action.payload;
  const component = yield select(selectors.getComponent, componentId);
  if (
    yield call(collaboratorsSagas.areQuoteItemsLocked, {
      componentIds: [componentId],
    })
  )
    return;

  const isRootComponent = component.isRootComponent;
  const hasCostAffectingChange = hasAnyProperty(data, [
    'processId',
    'materialId',
    'supplierMaterialId',
    'orientation',
  ]);
  const requiresBriefComponentUpdate =
    hasAnyProperty(data, [
      'partNumber',
      'revision',
      'workflowStatus',
      'description',
    ]) && !isRootComponent;
  const requiresAssemblyTreeUpdate = hasAnyProperty(data, [
    'partNumber',
    'revision',
    'workflowStatus',
  ]);
  const hasReqsReviewChange = hasAnyProperty(data, ['processId']);
  const hasBasicChange = hasAnyProperty(data, [
    'partNumber',
    'revision',
    'description',
    'workflowStatus',
  ]);
  if (hasBasicChange) {
    performance.mark(COMPONENT_BASIC_UPDATE.START);
  }

  try {
    yield put(pagePending.start());
    if (hasAnyProperty(data, ['partNumber'])) {
      queryClient.invalidateQueries({
        queryKey: getSimilarPartsKey(component.id),
      });
    }
    const response = yield call(
      quoteService.components.update,
      componentId,
      data,
    );

    if (hasReqsReviewChange) {
      // TODO: remove with https://paperlessparts.atlassian.net/browse/PP-23862
      // refresh actions since process changes can influence resolutions
      invalidateAllActionsQueries();
    }

    if (hasCostAffectingChange) {
      if (data.processId || data.materialId || data.supplierMaterialId) {
        yield put(componentInterrogation.request({ componentId }));
      }
      if (data.materialId || data.supplierMaterialId) {
        const { material } = response;
        if (material) {
          const newSupplierMaterial = {
            ...material,
            materialId: material.material.id,
          };
          yield put(
            receiveSupplierMaterialFromGetOrCreate({
              supplierMaterial: newSupplierMaterial,
            }),
          );
        }
      }

      // refetch component + update related rollup (if necessary)
      yield all([
        call(refreshComponent, componentId),
        put(
          refreshBomRollup.request({
            parentComponentIds: response.parentComponentIds,
          }),
        ),
      ]);
    }

    if (requiresBriefComponentUpdate) {
      yield call(refreshBriefComponent, componentId);
    }

    if (requiresAssemblyTreeUpdate) {
      yield put(assemblyTree.markNeedsUpdate({}));
    }

    if (hasBasicChange) {
      // log to datadog for performance monitoring when making basic changes to components
      const quoteItem = yield select(
        selectors.getQuoteItemById,
        response.quoteItemId,
      );
      performanceMeasureMarkFinished({
        labels: COMPONENT_BASIC_UPDATE,
        message: 'Basic component update finished',
        additionalFacets: {
          componentCount: (quoteItem?.componentIds || []).length,
          quantityCount: (response.quantities || []).length,
        },
      });
    }

    yield put(updateComponent.success({ component: response, data }));
  } catch (error) {
    yield put(updateComponent.error({ error, data }));
  } finally {
    yield put(pagePending.finish());
    if (hasCostAffectingChange) {
      yield put(componentUpdatePending.finish());
    }
  }
}

function* watchUpdateOperationOverride(action) {
  const { operationId, overrides, notes } = action.payload;
  try {
    let areAnyCellsUpdating = yield select(selectors.areAnyCellsUpdating);

    while (areAnyCellsUpdating) {
      yield race({
        error: take(types.UPDATE_CELL_ERROR),
        success: take(types.UPDATE_CELL_SUCCESS),
      });
      areAnyCellsUpdating = yield select(selectors.areAnyCellsUpdating);
    }

    let isOperationUpNext = yield select(
      selectors.getIsOperationNextInOverridesCalculationQueue,
      operationId,
    );
    while (!isOperationUpNext) {
      yield race({
        error: take(types.UPDATE_OPERATION_OVERRIDES_ERROR),
        success: take(types.UPDATE_OPERATION_SUCCESS),
      });
      isOperationUpNext = yield select(
        selectors.getIsOperationNextInOverridesCalculationQueue,
        operationId,
      );
    }

    const operation = yield select(selectors.getOperation, operationId);
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [operation.componentId],
      })
    )
      return;
    const updatedOperation = mergeOperationOrAddOnOverrides(
      operation,
      overrides,
    );

    if (notes !== undefined) {
      // cast empty string to null
      updatedOperation.notes = notes ?? null;
    }
    const response = yield call(
      service.operations.update,
      operationId,
      updatedOperation,
    );
    // Need to refresh operations because of workpiece dependency side effects
    const operations = yield call(
      service.operations.list,
      response.componentId,
    );
    yield put(updateOperation.success({ data: operations, operationId }));

    const updatingQueue = yield select(selectors.getOverridesUpdatingQueue);
    if (updatingQueue.length <= 1) {
      yield all([
        call(refreshQuantities, response.componentId),
        call(refreshBomRollupForComponentParentsById, {
          componentId: response.componentId,
        }),
      ]);
    }
  } catch (error) {
    console.error(error);
    yield put(updateOperationOverrides.error({ error, operationId }));
  } finally {
    if (yield cancelled()) {
      // NOTE: we have to dispatch a cancel action to remove the operationId
      //  from the calculation queue. This action can get cancelled routinely
      //  due to the takeLatestDeep implementations used to enable fast edits
      //  of setup time and runtime table cells.
      yield put(updateOperationOverrides.cancelRequest(action.payload));
    }
  }
}

// if the override's quantity-specific-ness and display context quantity-specific-ness don't match, remove
// that override from the overrides before even sending it to the backend
// this guarantees that if someone edits an operation with overrides, upon saving it will always have valid overrides
function mergeOperationOrAddOnOverrides(opOrAddon, overrides) {
  const newOverrides = {
    variables: {},
    drop_down_variables: {},
    table_variables: {},
  };
  forEachObjIndexed((overridenVar, varType) => {
    forEachObjIndexed((overrideValue, variableName) => {
      // find corresponding display context variable
      const displayContextVariable = pathOr(
        [],
        ['displayContext', 'variables'],
        opOrAddon,
      ).find((variable) => variable['secondaryKey'] === variableName);
      const displayContextQS = pathOr(
        null,
        ['quantitySpecific'],
        displayContextVariable || {},
      );
      const overrideQS =
        (typeof overrideValue === 'object' &&
          overrideValue !== null &&
          varType !== 'table_variables') ||
        (varType === 'table_variables' &&
          typeof overrideValue[Object.keys(overrideValue)[0]] === 'object' &&
          overrideValue[Object.keys(overrideValue)[0]] !== null);
      if (displayContextQS === overrideQS) {
        newOverrides[varType][variableName] = overrideValue;
      }
      if (newOverrides[varType] === []) {
        delete newOverrides[varType];
      }
    }, overridenVar);
  }, overrides);
  return dot.merge(opOrAddon, 'overrides', newOverrides);
}

function* watchSelectFinishOps(action) {
  try {
    const { value, componentId } = action.payload;
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    )
      return;
    if (value.type === 'op') {
      const op = yield select(selectors.getOperation, value.id);
      yield call(service.operations.update, op.id, {
        isFinish: !op.isFinish,
      });
    } else if (value.type === 'opDef') {
      const opDef = yield select(opDefSelectors.getOpDefById, value.id);
      yield call(service.operations.create, componentId, {
        name: opDef.name,
        opDefId: value.id,
        isFinish: true,
        isOutsideService: opDef.isOutsideService,
      });
    }

    // refresh operations due to workpiece side effects
    const operations = yield call(service.operations.list, componentId);
    const component = yield select(getComponentById, componentId);

    const nextComponent = {
      ...component,
      cells: operations.flatMap((op) => op.cells),
      operations: operations,
    };
    yield put(selectFinishOps.success(nextComponent));

    // broadcast a costing change
    const { part: partUuid, quoteItemId } = yield select(
      selectors.getComponentById,
      componentId,
    );
    broadcastComponentCostingChangeFromBaqToQpv({
      context: {
        partUuid,
        componentId,
        quoteItemId,
      },
    });

    yield all([
      call(refreshQuantities, componentId),
      call(refreshBomRollupForComponentParentsById, {
        componentId,
      }),
    ]);
  } catch (error) {
    yield put(selectFinishOps.error(error));
    yield call(toast, {
      status: 'error',
      title: 'Error',
      description: getErrorMessage(error),
    });
  }
}

function* refreshExpandedComponent() {
  const expandedComponentId = yield select(getExpandedComponentId);
  if (expandedComponentId) {
    const response = yield call(
      quoteService.components.get,
      expandedComponentId,
    );
    yield put(expandComponent.success({ component: response }));
  }
}

function* watchUpdateExpedite(action) {
  try {
    const { expediteId, data } = action.payload;
    const response = yield call(
      quoteService.quoteItems.updateExpedite,
      expediteId,
      data,
    );
    yield put(updateExpedite.success(response));
  } catch (error) {
    console.error(error);
    yield put(updateExpedite.error());
    yield call(toast, {
      status: 'error',
      title: 'Error',
      description: getErrorMessage(error),
    });
  }
}

function* watchCreateExpedite(action) {
  try {
    const { quantityId, data } = action.payload;
    const response = yield call(
      quoteService.quoteItems.createExpedite,
      quantityId,
      data,
    );
    const quantity = yield select(selectQuantity, quantityId);
    quantity.expedites.push(response);
    yield put(createExpedite.success(quantity));
  } catch (error) {
    console.error(error);
    yield put(createExpedite.error());
    yield call(toast, {
      status: 'error',
      title: 'Error',
      description: getErrorMessage(error),
    });
  }
}

function* watchDeleteExpedite(action) {
  const { quantityId, expediteId } = action.payload;
  try {
    yield call(quoteService.quoteItems.deleteExpedite, expediteId);
    const quantity = yield select(selectQuantity, quantityId);
    quantity.expedites = quantity.expedites.filter((id) => id !== expediteId);
    yield put(deleteExpedite.success(quantity));
  } catch (error) {
    console.error(error);
    yield put(deleteExpedite.error());
    yield call(toast, {
      status: 'error',
      title: 'Error',
      description: getErrorMessage(error),
    });
  }
}

function* watchUpdateSupplierMaterial(action) {
  const { materialId, supplierMaterial, partUuid, quoteItemId, componentId } =
    action.payload;
  try {
    const material = yield select(getMaterialById, materialId);
    yield put(materialModal.open({ material, supplierMaterial }));
    const winner = yield race({
      success: take(modalTypes.MATERIAL_MODAL_SAVE_SUCCESS),
      error: take(modalTypes.MATERIAL_MODAL_CLOSE),
    });

    if (winner.success) {
      yield put(componentUpdatePending.start());

      const { supplierMaterial } = winner.success.payload;
      yield put(receiveSupplierMaterialFromGetOrCreate({ supplierMaterial }));

      const expandedId = yield select(selectors.getExpandedQuoteItemId);
      yield put(
        refreshQuoteItem({
          quoteItemId: expandedId,
          shouldRecalculate: true,
        }),
      );
      broadcastComponentCostingChangeFromBaqToQpv({
        context: {
          partUuid,
          componentId,
          quoteItemId,
        },
      });
    }
    yield put(updateSupplierMaterial.success());
  } catch (error) {
    console.error(error);
    yield put(updateSupplierMaterial.error());
  } finally {
    yield put(componentUpdatePending.finish());
  }
}

function* addAddOnToStore(componentId, addOn) {
  const component = yield select(getComponentById, componentId);

  const nextComponent = {
    ...component,
    addOnCells: component.addOnCells.concat(addOn.addOnCells),
    addOns: component.addOns.concat([addOn]),
  };

  yield put(updateComponent.success({ component: nextComponent }));
}

function* watchCreateAddOn(action) {
  const { componentId, data } = action.payload;
  try {
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    ) {
      yield put(createAddOn.error());
      return;
    }
    const response = yield call(service.addOns.create, componentId, data);

    yield call(addAddOnToStore, componentId, response);
    yield put(createAddOn.success());
    yield call(refreshQuantities, componentId);
  } catch (error) {
    console.error(error);
    yield put(createAddOn.error(error));
  }
}

function* watchDeleteAddOn(action) {
  try {
    const { addOnId } = action.payload;
    const { componentId } = yield select(selectors.getAddOn, addOnId);
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    ) {
      yield put(deleteAddOn.error());
      return;
    }
    yield call(service.addOns.delete, addOnId);
    const component = yield select(getComponentById, componentId);
    const nextComponent = {
      ...component,
      addOns: component.addOns.filter((addOn) => addOn.id !== addOnId),
    };
    yield put(updateComponent.success({ component: nextComponent }));
    yield call(refreshQuantities, componentId);
  } catch (error) {
    yield put(deleteAddOn.error());
  }
}

function* watchUpdateAddOn(action) {
  const { addOnId, data } = action.payload;
  try {
    const { componentId } = yield select(selectors.getAddOn, addOnId);
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    ) {
      yield put(updateAddOn.error());
      return;
    }
    const response = yield call(service.addOns.update, addOnId, data);
    yield put(updateAddOn.success(response));
    yield call(refreshQuantities, componentId);
  } catch (error) {
    console.error(error);
    yield put(createAddOn.error(error));
  }
}

function* watchUpdateAddOnCell(action) {
  try {
    const { addOnCellId, data, cb } = action.payload;
    const { addOnId } = yield select(selectors.getAddOnCell, addOnCellId);

    const { componentId } = yield select(selectors.getAddOn, addOnId);
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    ) {
      yield put(updateAddOnCell.error());
      return;
    }
    const response = yield call(service.addOnCells.update, addOnCellId, data);

    if (cb) {
      cb();
    }
    yield put(updateAddOnCell.success(response));

    yield call(refreshQuantity, response.quantityId);
  } catch (error) {
    console.error(error);
    yield put(updateAddOnCell.error(error));
  }
}

function* watchUpdateAddOnOverrides(action) {
  const { addOnId, overrides, notes } = action.payload;
  try {
    const addOn = yield select(selectors.getAddOn, addOnId);
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [addOn.componentId],
      })
    )
      return;

    const response = yield call(service.addOns.update, addOnId, {
      overrides,
      notes,
    });
    yield put(updateAddOn.success(response));
    yield put(updateAddOnOverrides.success({ addOnId }));
    yield all([
      call(refreshAddOns, response.componentId),
      call(refreshQuantities, response.componentId),
    ]);
  } catch (error) {
    console.error(error);
    yield put(updateAddOnOverrides.error({ addOnId }));
  }
}

function* watchAddPurchasedComponents(action) {
  const { payload } = action;
  const { purchasedComponents, parentComponentId } = payload;

  try {
    const response = yield call(
      service.components.bulkAddPurchasedComponents,
      parentComponentId,
      {
        purchasedComponents,
      },
    );

    yield put(purchasedComponentAdd.success({ briefComponents: response }));

    yield call(toast, {
      status: 'success',
      title:
        `Added ${pluralize('component', purchasedComponents.length)} ` +
        response.map((c) => c.partNumber).join(', '),
    });

    yield put(assemblyTree.markNeedsUpdate({}));
    yield put(
      refreshBomRollup.request({
        parentComponentIds: response.flatMap((c) => c.parentComponentIds),
      }),
    );
  } catch (error) {
    yield put(purchasedComponentAdd.error(error));
  }
}

function* watchCreateQuoteItemWithManualPart() {
  const quoteSettings = yield call(detailsSettingsModalFlow, {
    isMultipleItems: false,
    mode: DETAILS_SETTINGS_MODAL_TYPES.QUOTE_ITEM_CREATE,
  });

  if (!quoteSettings) {
    return;
  }

  let quote;
  try {
    quote = yield call(getOrCreateQuote);
  } catch (error) {
    console.error(error);
    yield put(createQuoteItemWithManualPart.error(error));
    return;
  }

  const quoteId = quote.id;

  yield put(
    saveQuoteSettingsById({
      quoteWithSettingsId: quoteId,
      quoteSettings: quoteSettings || {},
    }),
  );

  // insert temporary quote items into the quote that optimistically updates
  // the loading state of the user, in case the file takes a long time to upload
  const oldQuoteItems = yield select(
    selectors.getQuoteItemsRespectingQuoteLoadingState,
  );

  const tempQuoteItem = constructTempQuoteItemData(
    quoteSettings.partNumber,
    oldQuoteItems.length + 1,
    quoteId,
  );
  const oldTempIds = yield select(selectors.getTemporaryQuoteItemIds);

  yield all([
    put(
      receiveTemporaryQuoteItemIds({
        temporaryQuoteItemIds: oldTempIds.concat([tempQuoteItem.id]),
      }),
    ),
    put(receiveQuoteItems(oldQuoteItems.concat([tempQuoteItem]))),
  ]);

  // create the new quote item with manual part data
  try {
    const data = {
      partNumber: quoteSettings.partNumber,
      processId: quoteSettings.process,
      materialId: quoteSettings.material,
      finishOpDefIds: quoteSettings.finishOpDefIds,
      quantities: quoteSettings.quantities,
      supplierMaterialId: quoteSettings.supplierMaterial,
      exportControlled: quoteSettings.exportControlled,
      estimatorId: quoteSettings.estimatorId,
      revision: quoteSettings.revision,
    };
    const response = yield call(service.createManualPart, quoteId, data);

    // add new quote item and remove corresponding temporary quote item
    tempQuoteItem.quoteId = null;
    yield put(
      receiveQuoteItems(oldQuoteItems.concat([tempQuoteItem, response])),
    );

    // clean up temporary quote item ids
    const currentTempIds = yield select(selectors.getTemporaryQuoteItemIds);
    yield put(
      receiveTemporaryQuoteItemIds({
        temporaryQuoteItemIds: currentTempIds.filter(
          (id) => id !== tempQuoteItem.id,
        ),
      }),
    );

    yield put(createQuoteItemWithManualPart.success());
  } catch (error) {
    // correct temp quote item reception
    yield put(receiveQuoteItems(oldQuoteItems));
    yield put(createQuoteItemWithManualPart.error(error));
  }
}

/**
 * This just handles the side effects of refreshing part and component data
 */
function* watchUpdatePartFilename(action) {
  try {
    const { componentId, partId } = action.payload;
    const result = yield call(partService.getForBAQ, partId);
    yield put(receivePart({ part: result }));
    yield call(refreshBriefComponent, componentId);
    queryClient.invalidateQueries({
      queryKey: getSimilarPartsKey(componentId),
    });

    yield put(assemblyTree.markNeedsUpdate({}));
    yield put(updatePartFilename.success({ componentId }));
  } catch (error) {
    yield put(
      updatePartFilename.error(
        error,
        pathOr(null, ['payload', 'componentId'], action),
      ),
    );
  }
}

function* watchComponentRegeneration(action) {
  const { componentId, quoteItemId } = action.payload;
  try {
    yield call(quoteService.components.regenerate, componentId);
    yield put(componentInterrogation.request({ componentId }));
    const newComponent = yield call(refreshComponent, componentId);
    yield put(
      refreshBomRollup.request({
        parentComponentIds: newComponent.parentComponentIds || [],
      }),
    );

    yield put(forceComponentRegeneration.success({ componentId, quoteItemId }));
  } catch (error) {
    yield put(forceComponentRegeneration.error({ componentId, quoteItemId }));
  }
}

function* watchUpdateQuoteItemWorkflowStep(action) {
  try {
    const { name, quoteItemId, status, workStepId } = action.payload;
    const quoteItem = yield call(service.setWorkflowStep, quoteItemId, {
      name,
      status,
      workStepId,
    });
    yield put(receiveQuoteItem(quoteItem));
    broadcastQuoteItemWorkflowChange({
      context: {
        quoteItemId,
        isRootComponent: true,
      },
    });
  } catch (error) {
    yield put(updateQuoteItemWorkflowStep.error(error));
  }
}

function* refreshProfitItems(componentId) {
  const profitItems = yield call(service.profitItems.list, componentId);
  yield put(receiveProfitItems(profitItems));
}

function* watchReorderProfitItems(action) {
  try {
    const { componentId, oldPosition, nextPosition } = action.payload;

    const component = yield select(getComponentById, componentId);

    const reorderedProfitItems = reorderByPosition({
      arrayToReorder: component.profitItems,
      oldPosition,
      nextPosition,
    });

    const nextComponent = {
      ...component,
      profitItems: reorderedProfitItems,
    };

    yield put(updateComponent.success({ component: nextComponent }));
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    )
      return;
    // actually update database
    const profitItem = component.profitItems.find(
      (profitItem) => profitItem.position === oldPosition,
    );
    yield call(service.profitItems.update, profitItem.id, {
      position: nextPosition >= oldPosition ? nextPosition - 1 : nextPosition,
    });

    yield all([
      call(refreshProfitItems, componentId),
      call(refreshQuantities, componentId),
    ]);
    yield put(reorderProfitItems.success());
  } catch (error) {
    yield put(reorderProfitItems.error(error));
  }
}

function* addProfitItemToStore(componentId, profitItem) {
  const component = yield select(getComponentById, componentId);

  const nextComponent = {
    ...component,
    profitItemCells: component.profitItemCells.concat(
      profitItem.profitItemCells,
    ),
    profitItems: component.profitItems.concat([profitItem]),
  };

  yield put(updateComponent.success({ component: nextComponent }));
}

function* watchCreateProfitItem(action) {
  const { componentId, data } = action.payload;
  try {
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    ) {
      yield put(createProfitItem.error());
      return;
    }
    const response = yield call(service.profitItems.create, componentId, data);

    yield call(addProfitItemToStore, componentId, response);
    yield put(createProfitItem.success());
    yield call(refreshQuantities, componentId);
  } catch (error) {
    console.error(error);
    yield put(createProfitItem.error(error));
  }
}

function* watchDeleteProfitItem(action) {
  try {
    const { profitItemId } = action.payload;
    const { componentId } = yield select(selectors.getProfitItem, profitItemId);
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    ) {
      yield put(deleteProfitItem.error());
      return;
    }
    yield call(service.profitItems.delete, profitItemId);
    const component = yield select(getComponentById, componentId);
    const nextComponent = {
      ...component,
      profitItems: component.profitItems.filter(
        (profitItem) => profitItem.id !== profitItemId,
      ),
    };
    yield put(updateComponent.success({ component: nextComponent }));
    yield call(refreshQuantities, componentId);
  } catch (error) {
    yield put(deleteProfitItem.error());
  }
}

function* watchUpdateProfitItem(action) {
  const { profitItemId, data } = action.payload;
  try {
    const { componentId } = yield select(selectors.getProfitItem, profitItemId);
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    ) {
      yield put(updateProfitItem.error());
      return;
    }
    const response = yield call(service.profitItems.update, profitItemId, data);
    yield put(updateProfitItem.success(response));
    yield call(refreshQuantities, componentId);
  } catch (error) {
    console.error(error);
    yield put(createProfitItem.error(error));
  }
}

function* watchUpdateProfitItemCell(action) {
  try {
    const { profitItemCellId, data, cb } = action.payload;
    const { profitItemId } = yield select(
      selectors.getProfitItemCell,
      profitItemCellId,
    );

    const { componentId } = yield select(selectors.getProfitItem, profitItemId);
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    ) {
      yield put(updateProfitItemCell.error());
      return;
    }
    const response = yield call(
      service.profitItemCells.update,
      profitItemCellId,
      data,
    );

    if (cb) {
      cb();
    }
    yield put(updateProfitItemCell.success(response));

    yield call(refreshQuantity, response.quantityId);
  } catch (error) {
    console.error(error);
    yield put(updateProfitItemCell.error(error));
  }
}

function* watchUpdateProfitItemOverrides(action) {
  const { profitItemId, overrides, notes } = action.payload;
  try {
    const profitItem = yield select(selectors.getProfitItem, profitItemId);
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [profitItem.componentId],
      })
    )
      return;

    const response = yield call(service.profitItems.update, profitItemId, {
      overrides,
      notes,
    });
    yield put(updateProfitItem.success(response));
    yield put(updateProfitItemOverrides.success({ profitItemId }));
    yield all([
      call(refreshProfitItems, response.componentId),
      call(refreshQuantities, response.componentId),
    ]);
  } catch (error) {
    console.error(error);
    yield put(updateProfitItemOverrides.error({ profitItemId }));
  }
}

function* refreshDiscounts(componentId) {
  const discounts = yield call(service.discounts.list, componentId);
  yield put(receiveDiscounts(discounts));
}

function* watchReorderDiscounts(action) {
  try {
    const { componentId, oldPosition, nextPosition } = action.payload;

    const component = yield select(getComponentById, componentId);

    const reorderedDiscounts = reorderByPosition({
      arrayToReorder: component.discounts,
      oldPosition,
      nextPosition,
    });

    const nextComponent = {
      ...component,
      discounts: reorderedDiscounts,
    };
    yield put(updateComponent.success({ component: nextComponent }));
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    )
      return;
    // actually update database
    const discount = component.discounts.find(
      (discount) => discount.position === oldPosition,
    );
    yield call(service.discounts.update, discount.id, {
      position: nextPosition >= oldPosition ? nextPosition - 1 : nextPosition,
    });

    yield all([
      call(refreshDiscounts, componentId),
      call(refreshQuantities, componentId),
    ]);
    yield put(reorderDiscounts.success());
  } catch (error) {
    yield put(reorderDiscounts.error(error));
  }
}

function* addDiscountToStore(componentId, discount) {
  const component = yield select(getComponentById, componentId);

  const nextComponent = {
    ...component,
    discountCells: component.discountCells.concat(discount.discountCells),
    discounts: component.discounts.concat([discount]),
  };

  yield put(updateComponent.success({ component: nextComponent }));
}

function* watchCreateDiscount(action) {
  const { componentId, data } = action.payload;
  try {
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    ) {
      yield put(createDiscount.error());
      return;
    }
    const response = yield call(service.discounts.create, componentId, data);

    yield call(addDiscountToStore, componentId, response);
    yield put(createDiscount.success());
    yield call(refreshQuantities, componentId);
  } catch (error) {
    console.error(error);
    yield put(createDiscount.error(error));
  }
}

function* watchDeleteDiscount(action) {
  try {
    const { discountId } = action.payload;
    const { componentId } = yield select(selectors.getDiscount, discountId);
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    ) {
      yield put(deleteDiscount.error());
      return;
    }
    yield call(service.discounts.delete, discountId);
    const component = yield select(getComponentById, componentId);
    const nextComponent = {
      ...component,
      discounts: component.discounts.filter(
        (discount) => discount.id !== discountId,
      ),
      discountCells: component.discountCells.filter(
        (cell) => cell.discountId !== discountId,
      ),
    };
    yield put(updateComponent.success({ component: nextComponent }));
    yield call(refreshQuantities, componentId);
  } catch (error) {
    yield put(deleteDiscount.error());
  }
}

function* watchUpdateDiscount(action) {
  const { discountId, data } = action.payload;
  try {
    const { componentId } = yield select(selectors.getDiscount, discountId);
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    ) {
      yield put(updateDiscount.error());
      return;
    }
    const response = yield call(service.discounts.update, discountId, data);
    yield put(updateDiscount.success(response));
    yield call(refreshQuantities, componentId);
  } catch (error) {
    console.error(error);
    yield put(createDiscount.error(error));
  }
}

function* watchUpdateDiscountCell(action) {
  try {
    const { discountCellId, data, cb } = action.payload;
    const { discountId } = yield select(
      selectors.getDiscountCell,
      discountCellId,
    );

    const { componentId } = yield select(selectors.getDiscount, discountId);
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [componentId],
      })
    ) {
      yield put(updateDiscountCell.error());
      return;
    }
    const response = yield call(
      service.discountCells.update,
      discountCellId,
      data,
    );

    if (cb) {
      cb();
    }
    yield put(updateDiscountCell.success(response));

    yield call(refreshQuantity, response.quantityId);
  } catch (error) {
    console.error(error);
    yield put(updateDiscountCell.error(error));
  }
}

function* watchUpdateDiscountOverrides(action) {
  const { discountId, overrides, notes } = action.payload;
  try {
    const discount = yield select(selectors.getDiscount, discountId);
    if (
      yield call(collaboratorsSagas.areQuoteItemsLocked, {
        componentIds: [discount.componentId],
      })
    )
      return;

    const response = yield call(service.discounts.update, discountId, {
      overrides,
      notes,
    });
    yield put(updateDiscount.success(response));
    yield put(updateDiscountOverrides.success({ discountId }));
    yield all([
      call(refreshDiscounts, response.componentId),
      call(refreshQuantities, response.componentId),
    ]);
  } catch (error) {
    console.error(error);
    yield put(updateDiscountOverrides.error({ discountId }));
  }
}

/**
 * Given a component id, grab the brief component from the redux store in order
 * to determine what the parent component ids are for that component, and then
 * dispatch a rollup refresh request. This is useful when performing an action on
 * a child component that will require the BOM table to refresh for a parent
 * component, such as editing a cell value, adding an operation, etc.
 * @param componentId
 * @param delayTime: time before the rollup refresh should occur, defaults to 250ms
 */
function* refreshBomRollupForComponentParentsById({
  componentId,
  delayTime = 250,
}) {
  try {
    const briefComponent = yield select(
      selectors.getBriefComponentById,
      componentId,
    );
    if (briefComponent) {
      const { parentComponentIds } = briefComponent;
      if (parentComponentIds.length > 0) {
        yield put(
          refreshBomRollup.request({
            parentComponentIds,
            delayTime,
          }),
        );
      }
    }
  } catch (error) {
    console.error(error);
  }
}

function* watchRefreshBomRollup({ payload }) {
  const {
    parentComponentIds: actionParentComponentIds,
    delayTime,
    shouldFullyRefreshRootOrExpandedComponents,
  } = payload;
  try {
    if (delayTime) {
      yield call(delay, delayTime);
    }
    const parentComponentIds = yield select(
      selectors.getComponentIdsToBomRollupRefreshArray,
    );

    const resultantParentCompIds = [
      ...new Set([
        ...parentComponentIds,
        ...actionParentComponentIds.map((cid) => cid.toString()),
      ]),
    ];

    yield call(refreshBomRollupFunc, {
      parentComponentIds: resultantParentCompIds,
      shouldFullyRefreshRootOrExpandedComponents,
    });
    yield put(refreshBomRollup.finish());
  } catch (error) {
    console.error(error);
  }
}

/**
 * Used to ensure that any actions performed on a child component have its side effects
 * handles up the BOM tree. This is managed by the bomTable query.
 * We will refresh quantities if the parent is expanded or the root, otherwise we will just
 * invalidate the bom table query to ensure it refetches next time it is requested.
 * @param parentComponentIds
 */
function* refreshBomRollupFunc({
  parentComponentIds,
  shouldFullyRefreshRootOrExpandedComponents,
}) {
  if (parentComponentIds.length > 0) {
    invalidateBomTableQueriesForComponentIds({
      componentIds: parentComponentIds,
    });
    const isExpandedOrRootById = yield select(
      selectors.getIsExpandedOrIsRootComponentForIds,
      parentComponentIds,
    );
    const actionCalls = [];
    for (const parentComponentId of parentComponentIds) {
      if (isExpandedOrRootById[parentComponentId]) {
        if (shouldFullyRefreshRootOrExpandedComponents) {
          actionCalls.push(call(refreshComponent, parentComponentId));
        } else {
          actionCalls.push(call(refreshQuantities, parentComponentId, true));
        }
      }
    }

    yield all(actionCalls);
  }
}

function* watchUpdateComponentQuoteItemForTabSyncing(action) {
  //broadcast changes after successful component update. (QPV is observing broadcasted changes and will update its partNumber and revision change)
  //Watching types.UPDATE_COMPONENT_SUCCESS, types.UPDATE_QUOTE_ITEM_SUCCESS actions because these are the final action after numerous locations that
  //trigger updateComponent.request() and updateQuoteItem.request(). (This is a choke point which makes it simpler to catch changes that need broadcasting
  //to other tabs/windows.)
  const {
    data: {
      partNumber,
      revision,
      description,
      exportControlled: isExportControlled,
      processId,
      materialId,
      supplierMaterialId,
      estimatorId,
      workflowStatus,
    },
    entities: { parts, quoteItemComponents },
  } = action.payload;
  const partUuids = parts ? Object.keys(parts) : [];
  if (!partUuids.length) {
    return;
  }
  const components = Object.values(quoteItemComponents);
  if (!components.length) {
    return;
  }
  const quote = yield select(getQuote);
  const { number: quoteNum, revisionNumber: quoteRevision } = quote ?? {};
  const didUpdateCosting =
    processId !== undefined ||
    materialId !== undefined ||
    supplierMaterialId !== undefined;

  if (didUpdateCosting) {
    for (const { quoteItemId, part: partUuid, id: componentId } of components) {
      const context = {
        partUuid,
        componentId,
        quoteItemId,
      };
      broadcastComponentCostingChangeFromBaqToQpv({ context });
    }
  }

  //Note: type is `PartDescriptorChangePayload` defined in 'modules/quotePartViewer/types'
  // Use partNumber != null to handle allow accepting empty string as broadcast value
  const payload = {
    ...(partNumber != null && { partNumber }),
    ...(revision != null && { revision }),
    ...(description != null && { description }),
  };
  if (Object.keys(payload).length) {
    for (const {
      quoteItemId,
      part: partUuid,
      id: componentId,
      isRootComponent,
    } of components) {
      const context = {
        isRootComponent,
        partUuid,
        componentId,
        quoteItemId,
        quoteNum,
        quoteRevision,
      };
      broadcastPartDescriptorChange({ context, payload });
    }
  }

  const didUpdateWorkflow =
    workflowStatus !== undefined || estimatorId !== undefined;
  if (didUpdateWorkflow) {
    for (const { quoteItemId, isRootComponent } of components) {
      if (isRootComponent) {
        const context = {
          quoteItemId,
          isRootComponent,
        };
        broadcastQuoteItemWorkflowChange({ context });
      }
    }
  }

  if (isExportControlled != null) {
    // NOTE: this should only ever be one quote item id, but deconstructing for defensive
    // programming
    const allQuoteItemIds = new Set(
      components.map(({ quoteItemId }) => quoteItemId),
    );
    allQuoteItemIds.forEach((quoteItemId) => {
      broadcastExportControlledChange({
        context: { quoteItemId },
        payload: { isExportControlled: isExportControlled },
      });
    });
  }
}
